import React, { useState, useEffect, useRef, useMemo } from 'react';
import DocumentHoverPreview from './DocumentHoverPreview';

interface TreeNode {
  name: string;
  type: 'file' | 'directory' | 'folder';
  path: string;
  size?: number;
  modified?: string;
  children?: TreeNode[];
}

interface SummarizeProgress {
  running: boolean;
  total?: number;
  skipped?: number;
  summarized?: number;
  errors?: number;
  done?: boolean;
}

interface FileTreeProps {
  clientId: string;
  onSelectFile: (filename: string) => void;
  selectedFile?: string;
  onCreateDocument?: () => void;
  onFileMove?: (from: string, to: string) => void;
  onRefresh?: () => void;
  onSummarizeAll?: () => Promise<{ total: number; skipped: number; summarized: number; errors: number }>;
}

// Couleurs par type de dossier
const FOLDER_COLORS: Record<string, { bg: string; icon: string; border: string }> = {
  'Documents_Officiels': { bg: 'bg-emerald-50', icon: 'text-emerald-600', border: 'border-emerald-200' },
  'Version_PDF': { bg: 'bg-red-50', icon: 'text-red-500', border: 'border-red-200' },
  'notes': { bg: 'bg-amber-50', icon: 'text-amber-600', border: 'border-amber-200' },
  'archives': { bg: 'bg-slate-100', icon: 'text-slate-500', border: 'border-slate-200' },
  'templates': { bg: 'bg-violet-50', icon: 'text-violet-600', border: 'border-violet-200' },
  'drafts': { bg: 'bg-orange-50', icon: 'text-orange-500', border: 'border-orange-200' },
  'brouillons': { bg: 'bg-orange-50', icon: 'text-orange-500', border: 'border-orange-200' },
  'images': { bg: 'bg-pink-50', icon: 'text-pink-500', border: 'border-pink-200' },
  'assets': { bg: 'bg-pink-50', icon: 'text-pink-500', border: 'border-pink-200' },
  'default': { bg: 'bg-indigo-50', icon: 'text-indigo-600', border: 'border-indigo-200' }
};

// Couleurs par extension de fichier
const FILE_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  'md': { bg: 'bg-sky-100', text: 'text-sky-700', label: 'MD' },
  'markdown': { bg: 'bg-sky-100', text: 'text-sky-700', label: 'MD' },
  'json': { bg: 'bg-amber-100', text: 'text-amber-700', label: 'JSON' },
  'pdf': { bg: 'bg-red-100', text: 'text-red-700', label: 'PDF' },
  'html': { bg: 'bg-orange-100', text: 'text-orange-700', label: 'HTML' },
  'htm': { bg: 'bg-orange-100', text: 'text-orange-700', label: 'HTML' },
  'css': { bg: 'bg-blue-100', text: 'text-blue-700', label: 'CSS' },
  'js': { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'JS' },
  'ts': { bg: 'bg-blue-100', text: 'text-blue-700', label: 'TS' },
  'csv': { bg: 'bg-green-100', text: 'text-green-700', label: 'CSV' },
  'xlsx': { bg: 'bg-green-100', text: 'text-green-700', label: 'XLS' },
  'xls': { bg: 'bg-green-100', text: 'text-green-700', label: 'XLS' },
  'docx': { bg: 'bg-blue-100', text: 'text-blue-700', label: 'DOC' },
  'doc': { bg: 'bg-blue-100', text: 'text-blue-700', label: 'DOC' },
  'png': { bg: 'bg-purple-100', text: 'text-purple-700', label: 'IMG' },
  'jpg': { bg: 'bg-purple-100', text: 'text-purple-700', label: 'IMG' },
  'jpeg': { bg: 'bg-purple-100', text: 'text-purple-700', label: 'IMG' },
  'gif': { bg: 'bg-purple-100', text: 'text-purple-700', label: 'IMG' },
  'webp': { bg: 'bg-purple-100', text: 'text-purple-700', label: 'IMG' },
  'svg': { bg: 'bg-purple-100', text: 'text-purple-700', label: 'SVG' },
  'txt': { bg: 'bg-gray-100', text: 'text-gray-600', label: 'TXT' },
  'default': { bg: 'bg-gray-100', text: 'text-gray-500', label: 'FILE' }
};

function TreeItem({
  node,
  depth,
  selectedFile,
  expandedFolders,
  onToggleFolder,
  onSelectFile,
  onHoverFile,
  onLeaveFile,
  onDrop,
  draggedFile,
  onDragStart,
  onDragEnd,
  onContextMenu,
  summaryPaths,
}: {
  node: TreeNode;
  depth: number;
  selectedFile?: string;
  expandedFolders: Set<string>;
  onToggleFolder: (path: string) => void;
  onSelectFile: (path: string) => void;
  onHoverFile?: (node: TreeNode, position: { x: number; y: number }) => void;
  onLeaveFile?: () => void;
  onDrop?: (targetFolder: string) => void;
  draggedFile?: string | null;
  onDragStart?: (path: string) => void;
  onDragEnd?: () => void;
  onContextMenu?: (e: React.MouseEvent, node: TreeNode) => void;
  summaryPaths?: Set<string>;
}) {
  const [isDragOver, setIsDragOver] = useState(false);
  const isFolder = node.type === 'directory' || node.type === 'folder';
  const isExpanded = expandedFolders.has(node.path);
  const isSelected = selectedFile === node.path;
  const isBeingDragged = draggedFile === node.path;

  const getFolderColors = () => {
    const lowerName = node.name.toLowerCase();
    for (const [key, colors] of Object.entries(FOLDER_COLORS)) {
      if (key !== 'default' && lowerName.includes(key.toLowerCase())) {
        return colors;
      }
    }
    return FOLDER_COLORS.default;
  };

  const getFileColors = () => {
    // Fiches _PDF.md ou _Word.md → afficher comme PDF
    if (node.name.includes('_PDF.md') || node.name.includes('_Word.md')) return FILE_COLORS['pdf'];
    const ext = node.name.split('.').pop()?.toLowerCase() || '';
    return FILE_COLORS[ext] || FILE_COLORS.default;
  };

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      const months = ['jan', 'fév', 'mar', 'avr', 'mai', 'jun', 'jul', 'aoû', 'sep', 'oct', 'nov', 'déc'];
      return `${date.getDate()} ${months[date.getMonth()]}`;
    } catch { return ''; }
  };

  const getDisplayName = (name: string): string => {
    // Nettoyer les suffixes _PDF et _Word avant l'extension
    return name.replace(/_(PDF|Word)\.(md)$/i, '.$2').replace(/\.[^/.]+$/, '');
  };

  const formatSize = (bytes?: number): string => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleMouseEnter = (e: React.MouseEvent) => {
    if (!isFolder && onHoverFile) {
      const rect = e.currentTarget.getBoundingClientRect();
      onHoverFile(node, { x: rect.right, y: rect.top + rect.height / 2 });
    }
  };

  const handleMouseLeave = () => {
    if (!isFolder && onLeaveFile) {
      onLeaveFile();
    }
  };

  const handleRightClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onContextMenu?.(e, node);
  };

  const folderColors = getFolderColors();
  const fileColors = getFileColors();

  return (
    <div className={depth > 0 ? 'ml-2 border-l-2 border-gray-100' : ''}>
      {isFolder ? (
        <button
          onClick={() => onToggleFolder(node.path)}
          onContextMenu={handleRightClick}
          onDragOver={(e) => {
            e.preventDefault();
            if (draggedFile && draggedFile !== node.path) {
              setIsDragOver(true);
            }
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragOver(false);
            if (draggedFile) {
              onDrop?.(node.path);
            }
          }}
          className={`w-full text-left my-1 rounded-xl transition-all duration-200 group overflow-hidden ${
            isDragOver
              ? 'bg-indigo-100 border-2 border-dashed border-indigo-400 ring-2 ring-indigo-200'
              : isExpanded
              ? `${folderColors.bg} border ${folderColors.border}`
              : 'hover:bg-gray-50'
          }`}
          style={{ marginLeft: depth > 0 ? '8px' : '0' }}
        >
          <div className="flex items-center gap-3 px-3 py-2.5">
            <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-all ${
              isExpanded ? 'bg-white shadow-sm' : 'bg-gray-100'
            }`}>
              <svg
                className={`w-3.5 h-3.5 transition-transform duration-300 ${
                  isExpanded ? folderColors.icon : 'text-gray-400'
                } ${isExpanded ? 'rotate-90' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
              </svg>
            </div>

            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
              isExpanded ? 'bg-white shadow-sm' : folderColors.bg
            }`}>
              <svg
                className={`w-5 h-5 ${folderColors.icon}`}
                fill={isExpanded ? 'none' : 'currentColor'}
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {isExpanded ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" />
                ) : (
                  <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                )}
              </svg>
            </div>

            <span className={`flex-1 font-semibold text-sm truncate ${
              isExpanded ? 'text-gray-900' : 'text-gray-700 group-hover:text-gray-900'
            }`}>
              {node.name}
            </span>

            {(node.name.toLowerCase().includes('officiel') || node.name.toLowerCase().includes('pdf')) && (
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${
                node.name.toLowerCase().includes('pdf') ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-600'
              }`}>
                {node.name.toLowerCase().includes('pdf') ? 'PDF' : 'Officiel'}
              </span>
            )}
          </div>
        </button>
      ) : (
        <button
          draggable
          onClick={() => onSelectFile(node.path)}
          onContextMenu={handleRightClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onDragStart={(e) => {
            e.dataTransfer.setData('text/plain', node.path);
            onDragStart?.(node.path);
          }}
          onDragEnd={() => onDragEnd?.()}
          className={`w-full text-left my-0.5 rounded-lg transition-all duration-200 group ${
            isSelected
              ? 'bg-gradient-to-r from-indigo-500 to-purple-600 shadow-md'
              : isBeingDragged
              ? 'opacity-50 bg-gray-100 border-2 border-dashed border-indigo-300'
              : 'hover:bg-gray-50'
          }`}
          style={{ marginLeft: depth > 0 ? '8px' : '0' }}
        >
          <div className="flex items-center gap-2.5 px-3 py-2">
            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider flex-shrink-0 ${
              isSelected ? 'bg-white/20 text-white' : `${fileColors.bg} ${fileColors.text}`
            }`}>
              {fileColors.label}
            </span>

            <span className={`flex-1 truncate text-sm ${
              isSelected ? 'text-white font-medium' : 'text-gray-600 group-hover:text-gray-900'
            }`}>
              {getDisplayName(node.name)}
            </span>

            {node.modified && (
              <span className={`text-[10px] flex-shrink-0 tabular-nums ${
                isSelected ? 'text-white/50' : 'text-gray-400'
              }`}>
                {formatDate(node.modified)}
              </span>
            )}

            {node.size && (
              <span className={`text-[10px] flex-shrink-0 tabular-nums ${
                isSelected ? 'text-white/60' : 'text-gray-400'
              }`}>
                {formatSize(node.size)}
              </span>
            )}

            {!isFolder && summaryPaths?.has(node.path) && (
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isSelected ? 'bg-green-300' : 'bg-emerald-400'}`} title="Résumé disponible" />
            )}
            {isSelected && (
              <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
            )}
          </div>
        </button>
      )}

      {isFolder && isExpanded && node.children && node.children.length > 0 && (
        <div className="relative">
          {node.children.map((child) => (
            <TreeItem
              key={child.path}
              node={child}
              depth={depth + 1}
              selectedFile={selectedFile}
              expandedFolders={expandedFolders}
              onToggleFolder={onToggleFolder}
              onSelectFile={onSelectFile}
              onHoverFile={onHoverFile}
              onLeaveFile={onLeaveFile}
              draggedFile={draggedFile}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onDrop={onDrop}
              onContextMenu={onContextMenu}
              summaryPaths={summaryPaths}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function FileTree({ clientId, onSelectFile, selectedFile, onCreateDocument, onFileMove, onRefresh, onSummarizeAll }: FileTreeProps) {
  const [tree, setTree] = useState<TreeNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [summaries, setSummaries] = useState<Record<string, { summary: string }>>({});
  const summaryPaths = useMemo(() => new Set(Object.keys(summaries)), [summaries]);
  const [draggedFile, setDraggedFile] = useState<string | null>(null);
  const [summarizeProgress, setSummarizeProgress] = useState<SummarizeProgress>({ running: false });

  // Hover preview state
  const [hoveredFile, setHoveredFile] = useState<TreeNode | null>(null);
  const [hoverPosition, setHoverPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; node: TreeNode } | null>(null);

  // Modal states
  const [renameModal, setRenameModal] = useState<{ node: TreeNode; newName: string } | null>(null);
  const [createFolderModal, setCreateFolderModal] = useState<{ parentPath: string; name: string } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<TreeNode | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Close context menu on click outside
  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const handleHoverFile = (node: TreeNode, position: { x: number; y: number }) => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredFile(node);
      setHoverPosition(position);
    }, 300);
  };

  const handleLeaveFile = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setHoveredFile(null);
  };

  const handleContextMenu = (e: React.MouseEvent, node: TreeNode) => {
    setContextMenu({ x: e.clientX, y: e.clientY, node });
  };

  useEffect(() => {
    fetchFiles();
    fetchSummaries();
  }, [clientId]);

  const fetchSummaries = async () => {
    try {
      const response = await fetch(`/api/portal/clients/${clientId}/summaries`);
      if (response.ok) {
        const data = await response.json();
        setSummaries(data);
      }
    } catch (err) {
      console.error('Erreur chargement résumés:', err);
    }
  };

  const buildTreeFromPaths = (docs: any[]): TreeNode[] => {
    const root: { [key: string]: TreeNode } = {};

    docs.forEach((doc) => {
      const relativePath = doc.relativePath || doc.name;
      const parts = relativePath.split('/');
      let current = root;

      parts.forEach((part: string, index: number) => {
        const isFile = index === parts.length - 1;
        const currentPath = parts.slice(0, index + 1).join('/');

        if (!current[part]) {
          current[part] = {
            name: part,
            type: isFile ? 'file' : 'directory',
            path: isFile ? relativePath : currentPath,
            size: isFile ? doc.size : undefined,
            modified: isFile ? doc.modified : undefined,
            children: isFile ? undefined : {},
          } as any;
        }

        if (!isFile) {
          current = (current[part] as any).children;
        }
      });
    });

    const convertToArray = (obj: { [key: string]: any }): TreeNode[] => {
      return Object.values(obj).map((node) => {
        if (node.children && typeof node.children === 'object') {
          return { ...node, children: convertToArray(node.children) };
        }
        return node;
      }).sort((a, b) => {
        const aIsFolder = a.type === 'directory' || a.type === 'folder';
        const bIsFolder = b.type === 'directory' || b.type === 'folder';

        const aIsOfficial = a.name.toLowerCase().includes('documents_officiel');
        const bIsOfficial = b.name.toLowerCase().includes('documents_officiel');
        if (aIsOfficial && !bIsOfficial) return -1;
        if (!aIsOfficial && bIsOfficial) return 1;

        if (aIsFolder && !bIsFolder) return -1;
        if (!aIsFolder && bIsFolder) return 1;

        return a.name.localeCompare(b.name);
      });
    };

    return convertToArray(root);
  };

  const fetchFiles = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`/api/portal/clients/${clientId}/docs`);
      if (!response.ok) throw new Error('Erreur chargement documents');
      const data = await response.json();

      if (data.tree && Array.isArray(data.tree)) {
        setTree(data.tree);
      } else if (data.docs && Array.isArray(data.docs)) {
        const treeData = buildTreeFromPaths(data.docs);
        setTree(treeData);
      } else {
        setTree([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFolder = (path: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const handleDragStart = (path: string) => setDraggedFile(path);
  const handleDragEnd = () => setDraggedFile(null);

  const handleFileDrop = async (targetFolder: string) => {
    if (!draggedFile || !onFileMove) return;

    const draggedDir = draggedFile.split('/').slice(0, -1).join('/');
    if (draggedDir === targetFolder) {
      setDraggedFile(null);
      return;
    }

    const filename = draggedFile.split('/').pop();
    const newPath = targetFolder ? `${targetFolder}/${filename}` : filename;

    try {
      await onFileMove(draggedFile, newPath || '');
      fetchFiles();
    } catch (err) {
      console.error('Erreur déplacement fichier:', err);
    }
    setDraggedFile(null);
  };

  // === ACTIONS ===

  const handleRename = async () => {
    if (!renameModal) return;
    setActionLoading(true);

    try {
      const isFolder = renameModal.node.type === 'directory' || renameModal.node.type === 'folder';
      const oldPath = renameModal.node.path;
      const parentPath = oldPath.split('/').slice(0, -1).join('/');
      const newPath = parentPath ? `${parentPath}/${renameModal.newName}` : renameModal.newName;

      const response = await fetch(`/api/portal/clients/${clientId}/docs/${encodeURIComponent(oldPath)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newName: newPath })
      });

      if (response.ok) {
        if (selectedFile === oldPath) {
          onSelectFile(newPath);
        }
        fetchFiles();
        onRefresh?.();
      } else {
        alert('Erreur lors du renommage');
      }
    } catch (err) {
      console.error('Erreur renommage:', err);
      alert('Erreur lors du renommage');
    } finally {
      setActionLoading(false);
      setRenameModal(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setActionLoading(true);

    try {
      const response = await fetch(`/api/portal/clients/${clientId}/docs/${encodeURIComponent(deleteConfirm.path)}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        fetchFiles();
        onRefresh?.();
      } else {
        alert('Erreur lors de la suppression');
      }
    } catch (err) {
      console.error('Erreur suppression:', err);
      alert('Erreur lors de la suppression');
    } finally {
      setActionLoading(false);
      setDeleteConfirm(null);
    }
  };

  const handleCreateFolder = async () => {
    if (!createFolderModal || !createFolderModal.name.trim()) return;
    setActionLoading(true);

    try {
      const folderPath = createFolderModal.parentPath
        ? `${createFolderModal.parentPath}/${createFolderModal.name}`
        : createFolderModal.name;

      const response = await fetch(`/api/portal/clients/${clientId}/folders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: folderPath })
      });

      if (response.ok) {
        fetchFiles();
        onRefresh?.();
      } else {
        alert('Erreur lors de la création du dossier');
      }
    } catch (err) {
      console.error('Erreur création dossier:', err);
      alert('Erreur lors de la création du dossier');
    } finally {
      setActionLoading(false);
      setCreateFolderModal(null);
    }
  };

  const countItems = (nodes: TreeNode[]): { files: number; folders: number } => {
    return nodes.reduce((acc, node) => {
      const isFolder = node.type === 'directory' || node.type === 'folder';
      if (isFolder) {
        acc.folders++;
        if (node.children) {
          const sub = countItems(node.children);
          acc.files += sub.files;
          acc.folders += sub.folders;
        }
      } else {
        acc.files++;
      }
      return acc;
    }, { files: 0, folders: 0 });
  };

  const expandAll = () => {
    const allFolders = new Set<string>();
    const collectFolders = (nodes: TreeNode[]) => {
      nodes.forEach((n) => {
        if (n.type === 'directory' || n.type === 'folder') {
          allFolders.add(n.path);
          if (n.children) collectFolders(n.children);
        }
      });
    };
    collectFolders(tree);
    setExpandedFolders(allFolders);
  };

  const handleSummarizeAll = async () => {
    if (!onSummarizeAll || summarizeProgress.running) return;
    setSummarizeProgress({ running: true });
    try {
      const result = await onSummarizeAll();
      setSummarizeProgress({ running: false, done: true, ...result });
      // Refresh summaries after generation
      fetchSummaries();
      // Clear done state after 4 seconds
      setTimeout(() => setSummarizeProgress({ running: false }), 4000);
    } catch (err) {
      console.error('Erreur résumé global:', err);
      setSummarizeProgress({ running: false });
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center animate-pulse">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
            </svg>
          </div>
          <span className="text-sm text-gray-500 font-medium">Chargement...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-red-100 flex items-center justify-center">
            <svg className="w-7 h-7 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <p className="text-sm text-red-600 font-semibold mb-4">{error}</p>
          <button
            onClick={fetchFiles}
            className="px-5 py-2.5 text-sm font-bold text-white rounded-xl shadow-lg transition-all hover:scale-105 hover:shadow-xl"
            style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  const counts = countItems(tree);

  return (
    <div className="h-full flex flex-col bg-white relative">
      {/* Header */}
      <div className="p-3 border-b border-gray-100">
        <div className="flex items-center gap-3 mb-3">
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-indigo-50 rounded-lg">
            <svg className="w-3.5 h-3.5 text-indigo-500" fill="currentColor" viewBox="0 0 24 24">
              <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
            </svg>
            <span className="text-xs font-bold text-indigo-600">{counts.folders}</span>
          </div>
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-sky-50 rounded-lg">
            <svg className="w-3.5 h-3.5 text-sky-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="text-xs font-bold text-sky-600">{counts.files}</span>
          </div>

          <div className="ml-auto flex gap-1">
            {/* Nouveau dossier */}
            <button
              onClick={() => setCreateFolderModal({ parentPath: '', name: '' })}
              className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all"
              title="Nouveau dossier"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
              </svg>
            </button>
            <button
              onClick={expandAll}
              className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
              title="Tout déplier"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <button
              onClick={() => setExpandedFolders(new Set())}
              className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
              title="Tout replier"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            </button>
            {onSummarizeAll && (
              <button
                onClick={handleSummarizeAll}
                disabled={summarizeProgress.running}
                className={`p-1.5 rounded-lg transition-all ${
                  summarizeProgress.running
                    ? 'text-amber-500 bg-amber-50 animate-pulse'
                    : summarizeProgress.done
                    ? 'text-green-500 bg-green-50'
                    : 'text-gray-400 hover:text-amber-500 hover:bg-amber-50'
                }`}
                title={
                  summarizeProgress.running
                    ? 'Résumés en cours...'
                    : summarizeProgress.done
                    ? `${summarizeProgress.summarized} résumé(s) généré(s)`
                    : 'Résumer tous les documents'
                }
              >
                {summarizeProgress.running ? (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={4} />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : summarizeProgress.done ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                )}
              </button>
            )}
            <button
              onClick={fetchFiles}
              className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
              title="Actualiser"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>

        {/* Summarize progress banner */}
        {(summarizeProgress.running || summarizeProgress.done) && (
          <div className={`px-3 py-2 text-xs font-semibold rounded-lg mx-0 mb-2 ${
            summarizeProgress.running
              ? 'bg-amber-50 text-amber-700 border border-amber-200'
              : summarizeProgress.errors && summarizeProgress.errors > 0
              ? 'bg-orange-50 text-orange-700 border border-orange-200'
              : 'bg-green-50 text-green-700 border border-green-200'
          }`}>
            {summarizeProgress.running ? (
              <span>Résumés IA en cours...</span>
            ) : (
              <span>
                {summarizeProgress.summarized} résumé(s) généré(s)
                {summarizeProgress.skipped ? `, ${summarizeProgress.skipped} existant(s)` : ''}
                {summarizeProgress.errors ? `, ${summarizeProgress.errors} erreur(s)` : ''}
              </span>
            )}
          </div>
        )}

        {onCreateDocument && (
          <button
            onClick={onCreateDocument}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 text-white text-sm font-bold rounded-xl transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl"
            style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Nouveau document
          </button>
        )}
      </div>

      {/* Tree */}
      <div className="flex-1 overflow-y-auto p-2">
        {tree.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full py-12">
            <div className="w-20 h-20 mb-4 rounded-2xl bg-gray-100 flex items-center justify-center">
              <svg className="w-10 h-10 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
              </svg>
            </div>
            <p className="text-sm font-semibold text-gray-400">Aucun document</p>
            <p className="text-xs text-gray-300 mt-1">Créez votre premier document</p>
          </div>
        ) : (
          <div className="space-y-0.5">
            {tree.map((node) => (
              <TreeItem
                key={node.path}
                node={node}
                depth={0}
                selectedFile={selectedFile}
                expandedFolders={expandedFolders}
                onToggleFolder={handleToggleFolder}
                onSelectFile={onSelectFile}
                onHoverFile={handleHoverFile}
                onLeaveFile={handleLeaveFile}
                draggedFile={draggedFile}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDrop={handleFileDrop}
                onContextMenu={handleContextMenu}
                summaryPaths={summaryPaths}
              />
            ))}
          </div>
        )}
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="fixed z-50 bg-white rounded-xl shadow-2xl border border-gray-200 py-2 min-w-[180px]"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-3 py-1.5 border-b border-gray-100 mb-1">
            <p className="text-xs font-semibold text-gray-500 truncate">{contextMenu.node.name}</p>
          </div>

          {/* Créer sous-dossier (si c'est un dossier) */}
          {(contextMenu.node.type === 'directory' || contextMenu.node.type === 'folder') && (
            <button
              onClick={() => {
                setCreateFolderModal({ parentPath: contextMenu.node.path, name: '' });
                setContextMenu(null);
              }}
              className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
              </svg>
              Nouveau sous-dossier
            </button>
          )}

          <button
            onClick={() => {
              setRenameModal({ node: contextMenu.node, newName: contextMenu.node.name });
              setContextMenu(null);
            }}
            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Renommer
          </button>

          <button
            onClick={() => {
              setDeleteConfirm(contextMenu.node);
              setContextMenu(null);
            }}
            className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Supprimer
          </button>
        </div>
      )}

      {/* Rename Modal */}
      {renameModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setRenameModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-[400px]" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-800 mb-4">Renommer</h3>
            <input
              type="text"
              value={renameModal.newName}
              onChange={(e) => setRenameModal({ ...renameModal, newName: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 text-gray-800"
              placeholder="Nouveau nom"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleRename()}
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setRenameModal(null)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleRename}
                disabled={actionLoading || !renameModal.newName.trim()}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {actionLoading ? 'Renommage...' : 'Renommer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Folder Modal */}
      {createFolderModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setCreateFolderModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-[400px]" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-800 mb-2">Nouveau dossier</h3>
            {createFolderModal.parentPath && (
              <p className="text-sm text-gray-500 mb-4">Dans: {createFolderModal.parentPath}</p>
            )}
            <input
              type="text"
              value={createFolderModal.name}
              onChange={(e) => setCreateFolderModal({ ...createFolderModal, name: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 text-gray-800"
              placeholder="Nom du dossier"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setCreateFolderModal(null)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleCreateFolder}
                disabled={actionLoading || !createFolderModal.name.trim()}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
              >
                {actionLoading ? 'Création...' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-[400px]" onClick={(e) => e.stopPropagation()}>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-gray-800 text-center mb-2">Supprimer ?</h3>
            <p className="text-sm text-gray-600 text-center mb-4">
              Voulez-vous vraiment supprimer <strong>{deleteConfirm.name}</strong> ?
              {(deleteConfirm.type === 'directory' || deleteConfirm.type === 'folder') && (
                <span className="block text-red-600 mt-1">Cela supprimera aussi tout son contenu.</span>
              )}
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                disabled={actionLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {actionLoading ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hover Preview */}
      {hoveredFile && (
        <DocumentHoverPreview
          filename={hoveredFile.name}
          summary={summaries[hoveredFile.path]?.summary}
          size={hoveredFile.size}
          modified={hoveredFile.modified}
          position={hoverPosition}
        />
      )}
    </div>
  );
}
